import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_RENTAL_PO_TRACKING,
  COLLECTION_ID_RENTAL_INVOICE_TRACKING,
  COLLECTION_ID_RENTAL_COMPANIES
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

const PO_LIMIT = 2000;
const INV_LIMIT = 5000;
const CO_LIMIT = 2000;

export function mapPoFromDoc(doc) {
  return {
    documentId: doc.$id,
    poNumber: doc.po_number,
    supplier: doc.supplier,
    project: doc.project,
    sd: doc.sd,
    totalPoAmount: typeof doc.total_po_amount === 'number' ? doc.total_po_amount : Number(doc.total_po_amount) || 0
  };
}

function mapPoToPayload(row) {
  return {
    po_number: row.poNumber,
    supplier: row.supplier,
    project: row.project,
    sd: row.sd,
    total_po_amount: row.totalPoAmount
  };
}

export function mapInvoiceFromDoc(doc) {
  const vat = doc.vat_applies;
  const invDate = doc.invoice_date;
  let invoiceDateStr = '';
  if (invDate instanceof Date && !Number.isNaN(invDate.getTime())) {
    const y = invDate.getFullYear();
    const m = String(invDate.getMonth() + 1).padStart(2, '0');
    const day = String(invDate.getDate()).padStart(2, '0');
    invoiceDateStr = `${y}-${m}-${day}`;
  } else if (invDate != null && invDate !== '') {
    invoiceDateStr = String(invDate);
  }
  return {
    documentId: doc.$id,
    invoiceNumber: doc.invoice_number,
    poNumber: doc.po_number,
    supplier: doc.supplier,
    project: doc.project,
    invoiceDate: invoiceDateStr,
    invoiceMonth: doc.invoice_month,
    amount: typeof doc.amount === 'number' ? doc.amount : Number(doc.amount) || 0,
    vatApplies: vat !== false && vat !== 'false',
    status: doc.status,
    datePaid: doc.date_paid || '',
    remark: doc.remark || '',
    prNo: doc.pr_no != null && doc.pr_no !== '' ? String(doc.pr_no).trim() : ''
  };
}

function mapInvoiceToPayload(row) {
  return {
    invoice_number: row.invoiceNumber,
    po_number: row.poNumber,
    supplier: row.supplier,
    project: row.project,
    invoice_date: row.invoiceDate,
    invoice_month: row.invoiceMonth,
    amount: row.amount,
    vat_applies: row.vatApplies !== false,
    status: row.status,
    date_paid: row.datePaid || '',
    remark: row.remark || '',
    pr_no: row.prNo != null && String(row.prNo).trim() !== '' ? String(row.prNo).trim() : ''
  };
}

export function mapCompanyFromDoc(doc) {
  return {
    documentId: doc.$id,
    rentalName: doc.rental_name,
    source: doc.source || ''
  };
}

async function listAllRentalDocuments(collectionId, maxDocs) {
  const all = [];
  let cursor = null;
  while (all.length < maxDocs) {
    const pageSize = Math.min(100, maxDocs - all.length);
    const queries = [Query.limit(pageSize)];
    if (cursor) queries.push(Query.cursorAfter(cursor));
    const res = await databases.listDocuments(DATABASE_ID, collectionId, queries);
    const docs = res.documents || [];
    all.push(...docs);
    if (docs.length < pageSize) break;
    cursor = docs[docs.length - 1].$id;
  }
  return all;
}

export async function fetchRentalPos() {
  try {
    const docs = await listAllRentalDocuments(COLLECTION_ID_RENTAL_PO_TRACKING, PO_LIMIT);
    return docs.map(mapPoFromDoc);
  } catch (e) {
    console.error('fetchRentalPos:', e);
    if (e.code === 404) {
      throw new Error(
        'Rental PO Tracking collection not found. Run: npm run setup:rental-trucking'
      );
    }
    throw e;
  }
}

export async function fetchRentalInvoices() {
  try {
    const docs = await listAllRentalDocuments(COLLECTION_ID_RENTAL_INVOICE_TRACKING, INV_LIMIT);
    return docs.map(mapInvoiceFromDoc);
  } catch (e) {
    console.error('fetchRentalInvoices:', e);
    if (e.code === 404) {
      throw new Error(
        'Rental Invoice Tracking collection not found. Run: npm run setup:rental-trucking'
      );
    }
    throw e;
  }
}

export async function fetchRentalCompanies() {
  try {
    const docs = await listAllRentalDocuments(COLLECTION_ID_RENTAL_COMPANIES, CO_LIMIT);
    return docs.map(mapCompanyFromDoc);
  } catch (e) {
    console.error('fetchRentalCompanies:', e);
    if (e.code === 404) {
      throw new Error(
        'Rental Companies collection not found. Run: npm run setup:rental-trucking'
      );
    }
    throw e;
  }
}

export async function createRentalPo(row) {
  const doc = await databases.createDocument(
    DATABASE_ID,
    COLLECTION_ID_RENTAL_PO_TRACKING,
    ID.unique(),
    mapPoToPayload(row)
  );
  return mapPoFromDoc(doc);
}

export async function updateRentalPo(documentId, row) {
  await databases.updateDocument(
    DATABASE_ID,
    COLLECTION_ID_RENTAL_PO_TRACKING,
    documentId,
    mapPoToPayload(row)
  );
}

export async function createRentalInvoice(row) {
  const doc = await databases.createDocument(
    DATABASE_ID,
    COLLECTION_ID_RENTAL_INVOICE_TRACKING,
    ID.unique(),
    mapInvoiceToPayload(row)
  );
  return mapInvoiceFromDoc(doc);
}

export async function updateRentalInvoice(documentId, row) {
  await databases.updateDocument(
    DATABASE_ID,
    COLLECTION_ID_RENTAL_INVOICE_TRACKING,
    documentId,
    mapInvoiceToPayload(row)
  );
}

export async function deleteRentalInvoice(documentId) {
  await databases.deleteDocument(DATABASE_ID, COLLECTION_ID_RENTAL_INVOICE_TRACKING, documentId);
}

export async function createRentalCompany(rentalName, source = 'manual') {
  const doc = await databases.createDocument(DATABASE_ID, COLLECTION_ID_RENTAL_COMPANIES, ID.unique(), {
    rental_name: rentalName.trim(),
    source
  });
  return mapCompanyFromDoc(doc);
}
